import api from "@/app/api";
import Guid from "@/app/types/common/guid";
import { Instant, LocalDate, LocalTime } from "@js-joda/core";
import {ModelObject, BaseModelObject, ValidationRule} from "@/app/services/modelObject";
import ServiceBase from "@/app/services/serviceBase";
import converters from "@/app/services/converters"

// Model Objects

export class TenantDepositSettings extends ModelObject<TenantDepositSettings> {

    public version: number = 0;
    /**
    * Whether the tenant partakes in bottle deposit scheme.
    */
    public isBottleDepositEnabled: boolean = false;

    public constructor(jsonSerializedObject?: any) {
        super();
        if (jsonSerializedObject) {
          this.version = jsonSerializedObject.version;
          this.isBottleDepositEnabled = jsonSerializedObject.isBottleDepositEnabled;
        }

    }

    public toJsonSerializedObject(): any {
        const toRet = {} as any;
        toRet.version = this.version;
        toRet.isBottleDepositEnabled = this.isBottleDepositEnabled;
        return toRet;
    }

    public toProto(): any {
      return this.toJsonSerializedObject();
    }

    public toBinary(): Uint8Array {
      return new TextEncoder().encode(JSON.stringify(this.toJsonSerializedObject()));
    }

    public validationRules: ValidationRule = [
            {field: "version", rule: (v: any) => v == null || v >= -9223372036854775808 || "Version is not greater than minimum allowed value: -9223372036854775808"},
            {field: "version", rule: (v: any) => v == null || v <= 9223372036854775807 || "Version is above maximum allowed value: 9223372036854775807"},
    ];
}


export class OutletDepositSettings extends ModelObject<OutletDepositSettings> {

    public version: number = 0;
    /**
    * Outlet with the setting.
    */
    public outletId: Guid = Guid.createEmpty();
    /**
    * Whether the outlet partakes in bottle deposit scheme.
    */
    public isBottleDepositEnabled: boolean = false;

    public constructor(jsonSerializedObject?: any) {
        super();
        if (jsonSerializedObject) {
          this.version = jsonSerializedObject.version;
          this.outletId = Guid.parseOrEmpty(jsonSerializedObject.outletId);
          this.isBottleDepositEnabled = jsonSerializedObject.isBottleDepositEnabled;
        }

    }

    public toJsonSerializedObject(): any {
        const toRet = {} as any;
        toRet.version = this.version;
        toRet.outletId = this.outletId.toString();
        toRet.isBottleDepositEnabled = this.isBottleDepositEnabled;
        return toRet;
    }

    public toProto(): any {
      return this.toJsonSerializedObject();
    }

    public toBinary(): Uint8Array {
      return new TextEncoder().encode(JSON.stringify(this.toJsonSerializedObject()));
    }

    public validationRules: ValidationRule = [
            {field: "version", rule: (v: any) => v == null || v >= -9223372036854775808 || "Version is not greater than minimum allowed value: -9223372036854775808"},
            {field: "version", rule: (v: any) => v == null || v <= 9223372036854775807 || "Version is above maximum allowed value: 9223372036854775807"},
            {field: "outletId", rule: (v: any) => !v.isEmpty() || "OutletDepositSettings.OutletId cannot be empty"},
    ];
}


export class BottleDepositSettingsService extends ServiceBase {

  public async getTenantSettings(tenantId: Guid, opts?: {requestTimeoutMs?: number, sessionToken?: string}): Promise<TenantDepositSettings> {
    const req = {
    tenantId:tenantId.toString(),
    };
    const extractor = (response:any) => new TenantDepositSettings(response.tenantDepositSettings);;
    return this.makeJsonRequest("bottleDepositSettings/v1/getTenantSettings", req, false, extractor, opts);
  };

  public async saveTenantSettings(tenantId: Guid, tenantDepositSettings: TenantDepositSettings, opts?: {requestTimeoutMs?: number, sessionToken?: string}): Promise<void> {
    const req = {
    tenantId:tenantId.toString(),
    tenantDepositSettings:tenantDepositSettings.toJsonSerializedObject(),
    };
    const extractor = null;
    return this.makeJsonRequest("bottleDepositSettings/v1/saveTenantSettings", req, true, extractor, opts);
  };

  public async getOutletSettings(tenantId: Guid, outletId: Guid, opts?: {requestTimeoutMs?: number, sessionToken?: string}): Promise<OutletDepositSettings> {
    const req = {
    tenantId:tenantId.toString(),
    outletId:outletId.toString(),
    };
    const extractor = (response:any) => new OutletDepositSettings(response.outletDepositSettings);;
    return this.makeJsonRequest("bottleDepositSettings/v1/getOutletSettings", req, false, extractor, opts);
  };

  public async getManyOutletSettings(tenantId: Guid, outletIds: readonly Guid[], opts?: {requestTimeoutMs?: number, sessionToken?: string}): Promise<OutletDepositSettings[]> {
    const req = {
    tenantId:tenantId.toString(),
    outletIds:outletIds.map(v => v.toString()),
    };
    const extractor = (response:any) => response.outletDepositSettings.map((v: any) => new OutletDepositSettings(v));;
    return this.makeJsonRequest("bottleDepositSettings/v1/getManyOutletSettings", req, false, extractor, opts);
  };

  public async saveOutletSettings(tenantId: Guid, outletDepositSettings: OutletDepositSettings, opts?: {requestTimeoutMs?: number, sessionToken?: string}): Promise<void> {
    const req = {
    tenantId:tenantId.toString(),
    outletDepositSettings:outletDepositSettings.toJsonSerializedObject(),
    };
    const extractor = null;
    return this.makeJsonRequest("bottleDepositSettings/v1/saveOutletSettings", req, true, extractor, opts);
  };
}

export const bottleDepositSettingsService = new BottleDepositSettingsService();